<template>
  <div
    class="faux-height sticky top-0 z-50"
    :style="fauxContainerStyle"
    ref="fauxContainer"
  >
    <div
      class="transition-bg pointer-events-none"
      :class="{ 'bg-white text-brown': !open && scrolled }"
    >
      <!-- Mobile Menu -->
      <div
        class="fixed top-0 left-0 w-full h-full bg-blue text-egg z-neg-1 flex flex-col justify-between items-center px-16 pb-16"
        :class="[open ? 'pointer-events-auto' : 'opacity-0']"
      >
        <div class="faux-height" />
        <!-- g1 -->
        <div class="flex flex-col text-center text-6-mob font-title">
          <RouterLink
            @click.native="open = false"
            class="mb-8 select-none"
            :to="{ name: 'About' }"
            >ABOUT</RouterLink
          >
          <RouterLink
            class="select-none"
            @click.native="open = false"
            :to="{ name: 'Stories' }"
            >STORIES</RouterLink
          >
        </div>
        <!-- g2 -->
        <div v-if="globals" class="text-center">
          <h2
            class="uppercase font-title text-1-mob mb-1"
            v-text="globals.instagram.heading"
          />
          <BlockInstagram class="text-5-mob" />
          <h2
            class="uppercase font-title text-1-mob mt-8 mb-1"
            v-text="globals.mailingList.heading"
          />
          <BlockSignUp class="text-5-mob" />
          <h2
            class="uppercase font-title text-1-mob mt-8 mb-1"
            v-text="globals.contact.heading"
          />
          <BlockEmail class="text-5-mob" />
        </div>
      </div>
      <!-- Header -->
      <div
        class="px-6 lg:px-10 transition-padding z-50 py-5 pointer-events-auto"
        :class="[
          scrolled ? 'lg:py-8' : 'lg:py-10',
          { 'border-b border-stone': !open && scrolled }
        ]"
      >
        <div class="flex justify-between text-3-desk font-title">
          <!-- LHS -->
          <CoreBurger
            :color="svgColor"
            class="ml-1 lg:hidden"
            @click="open = !open"
            :open="open"
          />
          <div class="hidden lg:flex items-center">
            <RouterLink :to="{ name: 'About' }" class="select-none"
              >ABOUT</RouterLink
            >
            <RouterLink :to="{ name: 'Stories' }" class="ml-16 select-none">
              STORIES
            </RouterLink>
          </div>
          <!-- CENTER -->
          <RouterLink @click.native="open = false" :to="{ name: 'Directory' }">
            <BlockLogotype
              class="transition-height"
              :color="svgColor"
              :height="logotypeHeight"
            />
          </RouterLink>
          <!-- RHS -->
          <div class="hidden lg:flex items-center">
            <span @click="handleFollow" class="cursor-pointer select-none"
              >FOLLOW</span
            >
            <span
              @click="handleContact"
              class="ml-16 cursor-pointer select-none"
              >CONTACT</span
            >
          </div>
          <!-- flex dummy div -->
          <div class="lg:hidden" />
        </div>
      </div>
      <!-- Filter Bar -->
      <div
        @click="filterOpen = false"
        class="fixed w-full h-full top-0 left-0 z-neg-1"
        :class="{ 'pointer-events-auto': filterOpen }"
      />
      <div
        v-if="$route.name === 'Directory'"
        class="transition-opacity"
        :class="[
          scrolled ? 'pointer-events-auto' : 'opacity-0',
          { 'opacity-0': open }
        ]"
        ref="filterBar"
      >
        <div
          class="h-10 flex items-center justify-center cursor-pointer"
          @click="filterOpen = !filterOpen"
        >
          <span class="select-none font-title text-2-mob lg:text-3-desk">
            FILTER {{ filterOpen ? "-" : "+" }}
          </span>
        </div>
        <CoreExpand class="absolute w-full" :open="filterOpen">
          <div class="bg-white w-full p-5 lg:p-8">
            <BlockFilter class="m-auto" backgroundColor="white" />
          </div>
        </CoreExpand>
      </div>
    </div>
  </div>
</template>

<script>
import debounce from "lodash.debounce";
import { mapState } from "vuex";
import { screens } from "@utils/tailwind";
import { bind, unbind } from "@utils/eventListener";
import { globals } from "@io/global";
import BlockLogotype from "./BlockLogotype";
import BlockSignUp from "./BlockSignUp";
import BlockInstagram from "./BlockInstagram";
import BlockEmail from "./BlockEmail";
import BlockFilter from "./BlockFilter";
import { handleFollow, handleContact } from "./BlockModalContainer";

export default {
  name: "BlockHeader",

  components: {
    BlockLogotype,
    BlockSignUp,
    BlockInstagram,
    BlockEmail,
    BlockFilter
  },

  data: () => ({
    scrolled: false,
    filterOpen: false,
    isXl: false,
    previousScrollTop: 0,
    menuOffset: 0,
    fauxHeight: 25 + 2.5 * 16
  }),

  computed: {
    globals,
    ...mapState({
      fgColor: state => state.ui.fgColor,
      isMobile: state => state.ui.isMobile
    }),
    open: {
      get() {
        return this.$store.state.ui.menuOpen;
      },
      set(val) {
        this.$store.commit("setMenuOpen", val);
      }
    },
    logotypeHeight() {
      if (this.isXl) return this.scrolled ? 25 : 32;

      if (this.isMobile) return 25;

      return this.scrolled ? 40 : 64;
    },
    svgColor() {
      if (this.open) return "egg";
      return this.scrolled ? "brown" : this.fgColor;
    },
    fauxContainerStyle() {
      return {
        ...(!this.open && { transform: `translateY(${this.menuOffset}px)` })
      };
    }
  },

  watch: {
    scrolled(val) {
      if (!val) {
        this.filterOpen = false;
      }
    },
    isMobile(val) {
      if (!val) this.open = false;
    }
  },

  mounted() {
    bind(window, "scroll", this.scrollHandler);

    this.debouncedIsXl = debounce(this.handleResize, 150);
    bind(window, "resize", this.debouncedIsXl);

    this.handleResize();
  },

  beforeDestroy() {
    unbind(window, "scroll", this.scrollHandler);
    unbind(window, "resize", this.debouncedIsXl);
  },

  methods: {
    handleFollow,
    handleContact,
    scrollHandler() {
      let { scrollTop } = document.documentElement;

      // Because Safari
      if (scrollTop <= 0) {
        scrollTop = 0;
      }

      const scrollDifference = this.previousScrollTop - scrollTop;

      this.menuOffset = this.menuOffset + scrollDifference;

      if (this.menuOffset * -1 >= this.fauxHeight) {
        this.menuOffset = -this.fauxHeight - 1;
      }

      if (this.menuOffset >= 0) {
        this.menuOffset = 0;
      }

      this.scrolled = scrollTop > 0;
      this.filterOpen = false;

      this.previousScrollTop = scrollTop;
    },
    handleResize() {
      this.isXl =
        window.innerWidth <
        parseInt(
          screens().find(breakpoint => breakpoint.handle === "xl").width
        );

      const filterBarHeight =
        this.$route.name === "Directory"
          ? this.$refs.filterBar.offsetHeight
          : 0;

      this.fauxHeight = this.$refs.fauxContainer.offsetHeight + filterBarHeight;
    }
  }
};
</script>

<style scoped>
.transition-padding {
  transition-property: padding-top, padding-bottom;
  transition-duration: var(--transition-long);
  transition-timing-function: var(--transition-ease-out);
}

.transition-height {
  transition-property: height;
  transition-duration: var(--transition-long);
  transition-timing-function: var(--transition-ease-out);
}

.transition-bg {
  transition-property: background-color;
  transition-duration: var(--transition-long);
  transition-timing-function: var(--transition-ease-out);
}

.transition-opacity {
  transition-property: opacity;
  transition-duration: var(--transition-long);
  transition-timing-function: var(--transition-ease-out);
}

.faux-height {
  height: calc(25px + 2.5rem);
}

@screen lg {
  .faux-height {
    height: calc(32px + 5rem);
  }
}

@screen xl {
  .faux-height {
    height: calc(64px + 5rem);
  }
}
</style>
